import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, Bot, ReceiptText, Users, LogOut, Menu, X, Crown,
  Gauge, UserCog, Wallet, Banknote, Layers, Youtube, HandCoins, ScrollText, Settings, LifeBuoy,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const USER_NAV = [
  ["/dashboard", "Dashboard", LayoutDashboard],
  ["/deposit", "Deposit", ArrowDownToLine],
  ["/withdraw", "Withdraw", ArrowUpFromLine],
  ["/plans", "AI Plans", Bot],
  ["/transactions", "Transactions", ReceiptText],
  ["/referrals", "Referrals", Users],
];

const ADMIN_NAV = [
  ["/admin", "Overview", Gauge],
  ["/admin/users", "Users", UserCog],
  ["/admin/deposits", "Deposits", Wallet],
  ["/admin/withdrawals", "Withdrawals", Banknote],
  ["/admin/plans", "Plans & Returns", Layers],
  ["/admin/youtubers", "YouTubers", Youtube],
  ["/admin/commissions", "Commissions", HandCoins],
  ["/admin/tickets", "Support Tickets", LifeBuoy],
  ["/admin/transactions", "Transactions", ScrollText],
  ["/admin/settings", "Settings", Settings],
];

export const Logo = () => (
  <div className="flex items-center gap-2.5">
    <div className="grid h-8 w-8 place-items-center rounded-lg bg-[#10B981]/15 border border-[#10B981]/40">
      <span className="font-display text-sm font-bold text-[#10B981]">N</span>
    </div>
    <span className="font-display text-lg font-semibold tracking-tight text-white">AI Nexora</span>
  </div>
);

const BADGE_KEYS = { "/admin/tickets": "open_tickets", "/admin/deposits": "pending_deposits" };

function useBadges(admin) {
  const [b, setB] = useState({});
  useEffect(() => {
    if (!admin) return undefined;
    const load = () => api.get("/admin/badges").then((r) => setB(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [admin]);
  return b;
}

function NavItems({ admin, isYt, onNavigate }) {
  const nav = admin ? ADMIN_NAV : USER_NAV;
  const badges = useBadges(admin);
  return (
    <nav className="flex flex-col gap-1">
      {nav.map(([to, label, Icon]) => {
        const text = !admin && to === "/referrals" && isYt ? "Partner Hub" : label;
        const Ico = !admin && to === "/referrals" && isYt ? Crown : Icon;
        return (
          <NavLink key={to} to={to} end={to === "/admin"} onClick={onNavigate}
            data-testid={`nav-${text.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
              isActive ? "bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/25" : "text-slate-400 border border-transparent hover:bg-[#1A2232] hover:text-white"}`}>
            <Ico size={17} />
            {text}
            {badges[BADGE_KEYS[to]] > 0 && (
              <span className="num ml-auto rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-400" data-testid={`nav-badge-${BADGE_KEYS[to]}`}>{badges[BADGE_KEYS[to]]}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function SideContent({ admin, onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col px-4 py-6">
      <div className="px-2 mb-8"><Logo /></div>
      {admin && <p className="px-3 mb-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">Admin Control</p>}
      <NavItems admin={admin} isYt={user?.is_youtuber} onNavigate={onNavigate} />
      <div className="mt-auto rounded-xl border border-[#1E2738] bg-[#0B0E14] p-3">
        <p className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">{user?.name}</p>
        <p className="truncate text-xs text-slate-500">{user?.email}</p>
        <p className="num mt-1 text-xs text-[#10B981]" data-testid="sidebar-user-id">ID: {user?.user_code}</p>
        <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-[#242F42] py-2 text-xs text-slate-300 hover:bg-[#1A2232]">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function Shell({ admin = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-[#1E2738] bg-[#0D1119] lg:block">
        <SideContent admin={admin} />
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#1E2738] bg-[#0B0E14]/90 px-4 py-3 backdrop-blur-md lg:hidden">
        <Logo />
        <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="rounded-lg border border-[#242F42] p-2 text-slate-300"><Menu size={18} /></button>
      </header>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 border-r border-[#1E2738] bg-[#0D1119]">
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="absolute right-3 top-5 p-1 text-slate-400"><X size={18} /></button>
            <SideContent admin={admin} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-10"><Outlet /></div>
      </main>
    </div>
  );
}
